/* This is an entrypoint for multi-select in the content tree. It opens the BulkActionsMenu, which can be found in @confluence/page-tree */
import React, { useEffect, useId, useRef } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import IconButton from '@atlaskit/button/icon/button';
import TasksIcon from '@atlaskit/icon/core/tasks';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { PERSISTED_KEYS_ON_SERVER } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

type MultiSelectButtonProps = {
	onClick: () => void;
	isDisabled: boolean;
	spaceHasUserContent: boolean;
	isPageTreeEmpty: boolean;
	isMultiSelectEnabled: boolean;
	isLoading: boolean;
	isMobile: boolean;
	isFilterActive?: boolean;
};

export const MultiSelectButton = ({
	onClick,
	isDisabled,
	spaceHasUserContent,
	isPageTreeEmpty,
	isMultiSelectEnabled,
	isLoading,
	isMobile,
	isFilterActive = false,
}: MultiSelectButtonProps): React.JSX.Element | null => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const hasFiredScreenEvent = useRef(false);
	const descriptionId = useId();

	// only fire the screen event once when the sidebar is expanded
	useEffect(() => {
		const tryFireScreenEvent = () => {
			if (
				localStorage.getItem(PERSISTED_KEYS_ON_SERVER.PERSISTED_LEFT_SIDEBAR_COLLAPSED) ===
					'expanded' &&
				!hasFiredScreenEvent.current
			) {
				hasFiredScreenEvent.current = true;
				createAnalyticsEvent({
					type: 'sendScreenEvent',
					data: {
						name: 'multiSelectButton',
					},
				}).fire();
			}
		};

		tryFireScreenEvent();

		window.addEventListener('resize', tryFireScreenEvent);
		return () => {
			window.removeEventListener('resize', tryFireScreenEvent);
		};
	}, [createAnalyticsEvent]);

	if (!spaceHasUserContent || isPageTreeEmpty || isMobile || !isMultiSelectEnabled) {
		return null;
	}

	return (
		<>
			<IconButton
				icon={TasksIcon}
				label={intl.formatMessage(i18n.enableMultiSelectLabel)}
				aria-describedby={descriptionId}
				appearance="subtle"
				spacing="compact"
				onClick={onClick}
				isTooltipDisabled={false}
				isDisabled={isLoading || isDisabled || isFilterActive}
				tooltip={{
					content: isFilterActive
						? intl.formatMessage(i18n.disabledMultiSelectTooltipOnFilter)
						: isDisabled
							? intl.formatMessage(i18n.disabledMultiSelectTooltipOnSearch)
							: intl.formatMessage(i18n.enableMultiSelect),
				}}
			/>
			<VisuallyHidden id={descriptionId}>
				{intl.formatMessage(i18n.enableMultiSelectPermissionNote)}
			</VisuallyHidden>
		</>
	);
};

type ExitMultiSelectButtonProps = {
	onClick: () => void;
};

export const ExitMultiSelectButton = ({
	onClick,
}: ExitMultiSelectButtonProps): React.JSX.Element => {
	const intl = useIntl();

	return (
		<Pressable
			aria-label={intl.formatMessage(i18n.exitMultiSelectLabel)}
			onClick={onClick}
			xcss={styles.exit}
			analyticsContext={{
				source: 'contentTreeBulkActions',
				attributes: {
					name: 'exitMultiSelectButton',
				},
			}}
		>
			{intl.formatMessage(i18n.exitMultiSelect)}
		</Pressable>
	);
};

const i18n = defineMessages({
	exitMultiSelect: {
		id: 'space-views.exit-multi-select',
		defaultMessage: 'Exit',
		description: 'Text to exit the multi-select mode and exit bulk actions in the content tree',
	},
	exitMultiSelectLabel: {
		id: 'space-views.exit-multi-select-label',
		defaultMessage: 'Exit multi-select mode',
		description: 'Label for the button to exit multi-select mode in the content tree',
	},
	enableMultiSelectLabel: {
		id: 'space-views.enable-multi-select-label',
		defaultMessage: 'Select multiple items',
		description:
			'Accessible name for the button that enables multi-select mode in the content tree',
	},
	enableMultiSelect: {
		id: 'space-views.enable-multi-select',
		defaultMessage:
			'Select multiple items to edit. Only items you have permission to modify will be affected.',
		description:
			'Tooltip shown on hover or focus of the button that enables multi-select mode in the content tree',
	},
	enableMultiSelectPermissionNote: {
		id: 'space-views.enable-multi-select-permission-note',
		defaultMessage: 'Only items you have permission to modify will be affected.',
		description:
			'Screen-reader description for the button that enables multi-select mode in the content tree. Conveys the caveat that is otherwise only visible in the tooltip, without lengthening the button name.',
	},
	disabledMultiSelectTooltipOnSearch: {
		id: 'space-views.disabled-multi-select-tooltip',
		defaultMessage: 'Multi-select is disabled when searching',
		description: 'Tooltip for the disabled multi-select button when search is active',
	},
	disabledMultiSelectTooltipOnFilter: {
		id: 'space-views.disabled-multi-select-tooltip-on-filter',
		defaultMessage: 'Multi-select is disabled when filtering',
		description:
			'Tooltip for the disabled multi-select button when a sort or filter is applied to the content tree',
	},
});

const styles = cssMap({
	exit: {
		zIndex: 1,
		height: '20px',
		font: token('font.body'),
		paddingInline: token('space.050'),
		backgroundColor: token('color.background.neutral.subtle'),
		color: token('color.text.selected'),
		'&:hover': {
			textDecoration: 'underline',
		},
	},
});
